import React from 'react';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../store/hooks';
import { metaDataSelector } from '../../store/globalSelectors';
import { setPatientInformation } from '../../store/slices/metaDataSlice';
import {
  heightConversion,
  weightConversion
} from '../../shared/utils/utilFunctions';
import { DoubleNumberCard } from '../../shared/components/numberInput/numberCards/doubleNumberCard';

export const ImperialView = () => {
  const { patientInformation } = useAppSelector(metaDataSelector);
  const dispatch = useDispatch();

  return (
    <>
      <DoubleNumberCard
        title={'HEIGHT'}
        subHead1={'(ft)'}
        subHead2={'(in)'}
        decimalRequired1={true}
        decimalRequired2={true}
        range1={{ min: 0, max: 20 }}
        range2={{ min: 0, max: 12 }}
        value1={patientInformation.imperial.height.ft}
        value2={patientInformation.imperial.height.in}
        setValue1={(val: number) =>
          dispatch(
            setPatientInformation({
              ...patientInformation,
              metric: {
                ...patientInformation.metric,
                height: heightConversion(
                  Number(val),
                  patientInformation.imperial.height.in
                )
              },
              imperial: {
                ...patientInformation.imperial,
                height: {
                  ...patientInformation.imperial.height,
                  ft: Number(val)
                }
              }
            })
          )
        }
        setValue2={(val: number) => {
          const ftHeight = patientInformation.imperial.height.ft;
          const updatedInHeight = Number(val) === 12 ? 0 : Number(val);
          const updatedFtHeight =
            Number(val) === 12 && ftHeight + 1 <= 20 ? ftHeight + 1 : ftHeight;
          dispatch(
            setPatientInformation({
              ...patientInformation,
              metric: {
                ...patientInformation.metric,
                height: heightConversion(updatedFtHeight, updatedInHeight)
              },
              imperial: {
                ...patientInformation.imperial,
                height: {
                  ft: updatedFtHeight,
                  in: updatedInHeight
                }
              }
            })
          );
        }}
      />

      <DoubleNumberCard
        title={'WEIGHT'}
        subHead1={'(st)'}
        subHead2={'(lb)'}
        decimalRequired1={true}
        decimalRequired2={true}
        range1={{ min: 0, max: 30 }}
        range2={{ min: 0, max: 13 }}
        value1={patientInformation.imperial.weight.st}
        value2={patientInformation.imperial.weight.lb}
        setValue1={(val: number) =>
          dispatch(
            setPatientInformation({
              ...patientInformation,
              metric: {
                ...patientInformation.metric,
                weight: weightConversion(
                  Number(val),
                  patientInformation.imperial.weight.lb
                )
              },
              imperial: {
                ...patientInformation.imperial,
                weight: {
                  ...patientInformation.imperial.weight,
                  st: Number(val)
                }
              }
            })
          )
        }
        setValue2={(val: number) => {
          const stWeight = patientInformation.imperial.weight.st;
          const updatedLbWeight = Number(val) === 13 ? 0 : Number(val);
          const updatedStWeight =
            Number(val) === 13 && stWeight + 1 <= 30 ? stWeight + 1 : stWeight;
          dispatch(
            setPatientInformation({
              ...patientInformation,
              metric: {
                ...patientInformation.metric,
                weight: weightConversion(updatedStWeight, updatedLbWeight)
              },
              imperial: {
                ...patientInformation.imperial,
                weight: {
                  st: updatedStWeight,
                  lb: updatedLbWeight
                }
              }
            })
          );
        }}
      />

      <DoubleNumberCard
        title={'USUAL WEIGHT'}
        subHead1={'(st)'}
        subHead2={'(lb)'}
        subHead3={'(in the past 3-6 months)'}
        decimalRequired1={true}
        decimalRequired2={true}
        range1={{ min: 0, max: 30 }}
        range2={{ min: 0, max: 13 }}
        value1={patientInformation.imperial.usualWeight.st}
        value2={patientInformation.imperial.usualWeight.lb}
        setValue1={(val: number) =>
          dispatch(
            setPatientInformation({
              ...patientInformation,
              metric: {
                ...patientInformation.metric,
                usualWeight: weightConversion(
                  Number(val),
                  patientInformation.imperial.usualWeight.lb
                )
              },
              imperial: {
                ...patientInformation.imperial,
                usualWeight: {
                  ...patientInformation.imperial.usualWeight,
                  st: Number(val)
                }
              }
            })
          )
        }
        setValue2={(val: number) => {
          const stWeight = patientInformation.imperial.usualWeight.st;
          const updatedLbWeight = Number(val) === 13 ? 0 : Number(val);
          const updatedStWeight =
            Number(val) === 13 && stWeight + 1 <= 30 ? stWeight + 1 : stWeight;
          dispatch(
            setPatientInformation({
              ...patientInformation,
              metric: {
                ...patientInformation.metric,
                usualWeight: weightConversion(updatedStWeight, updatedLbWeight)
              },
              imperial: {
                ...patientInformation.imperial,
                usualWeight: {
                  st: updatedStWeight,
                  lb: updatedLbWeight
                }
              }
            })
          );
        }}
      />

      <DoubleNumberCard
        title={'ESTIMATED DRY WEIGHT'}
        subHead1={'(st)'}
        subHead2={'(lb)'}
        decimalRequired1={true}
        decimalRequired2={true}
        range1={{ min: 0, max: 30 }}
        range2={{ min: 0, max: 13 }}
        value1={patientInformation.imperial.estimatedDryWeight.st}
        value2={patientInformation.imperial.estimatedDryWeight.lb}
        setValue1={(val: number) =>
          dispatch(
            setPatientInformation({
              ...patientInformation,
              metric: {
                ...patientInformation.metric,
                estimatedDryWeight: weightConversion(
                  Number(val),
                  patientInformation.imperial.estimatedDryWeight.lb
                )
              },
              imperial: {
                ...patientInformation.imperial,
                estimatedDryWeight: {
                  ...patientInformation.imperial.estimatedDryWeight,
                  st: Number(val)
                }
              }
            })
          )
        }
        setValue2={(val: number) => {
          const stWeight = patientInformation.imperial.estimatedDryWeight.st;
          const updatedLbWeight = Number(val) === 13 ? 0 : Number(val);
          const updatedStWeight =
            Number(val) === 13 && stWeight + 1 <= 30 ? stWeight + 1 : stWeight;
          dispatch(
            setPatientInformation({
              ...patientInformation,
              metric: {
                ...patientInformation.metric,
                estimatedDryWeight: weightConversion(
                  updatedStWeight,
                  updatedLbWeight
                )
              },
              imperial: {
                ...patientInformation.imperial,
                estimatedDryWeight: {
                  st: updatedStWeight,
                  lb: updatedLbWeight
                }
              }
            })
          );
        }}
      />
    </>
  );
};
